import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';

import { decide, type PatientResponse } from '@emr/shared';

import {
  listPatientDuplicates,
  resolvePatientDuplicate,
  type PatientDuplicateCandidateResponse,
} from '../../api/patientDuplicates.ts';
import { searchPatients } from '../../api/patients.ts';
import { useGrants } from '../../auth/sessionContext.ts';
import { GatedRoute } from '../../components/GatedRoute.tsx';
import { Badge, Button, ErrorText, Panel } from '../../components/primitives.tsx';
import { QuerySection } from '../../components/QuerySection.tsx';
import { formatCalendarDate } from '../../lib/dateFormat.ts';

/**
 * The duplicate-patient-record review queue (`BACKLOG.md` C2a) — every pending candidate pair
 * `@emr/shared`'s `findLikelyDuplicates` surfaced when each was created, with a confirm/reject
 * action for staff. Reached from `PatientListPage.tsx`'s own header link, per this task's own
 * "reachable, not orphaned" requirement.
 *
 * **Gated on `patient:read`/`patient:write`, not `chart:read`/`chart:write`** — matching
 * `patientDuplicatesRoutes.ts`'s own header: this is administrative metadata about two patient
 * *records*, the same register as `PatientListPage.tsx` itself, not clinical chart content.
 *
 * **Confirming a pair does not merge any chart data.** It records a staff decision — see
 * `db/schema/clinical.ts`'s own comment on `patientDuplicateCandidates` for why an actual data merge
 * is deliberately out of scope for this pass. The confirm-duplicate action is worded accordingly.
 */
export function PatientDuplicatesPage() {
  const { t } = useTranslation('patientDuplicates');
  return (
    <GatedRoute title={t('pageTitle')} capability="patient:read" feature={null}>
      <DuplicatesQueue />
    </GatedRoute>
  );
}

type ResolveStatus = 'confirmed_duplicate' | 'confirmed_distinct';

function DuplicatesQueue() {
  const { t } = useTranslation('patientDuplicates');
  const grants = useGrants();
  const mayResolve = decide(grants, 'patient:write');
  const queryClient = useQueryClient();
  const queryKey = ['patients', 'duplicates'];

  const duplicatesQuery = useQuery({
    queryKey,
    queryFn: ({ signal }) => listPatientDuplicates(signal),
  });

  // Every distinct patient id named by any candidate pair, resolved through the existing batch-by-id
  // lookup (`GET /api/patients?ids=`, `BACKLOG.md` K5) rather than this response carrying either
  // patient's own demographics a second time — see `patientDuplicateCandidateResponseSchema`'s own
  // comment.
  const patientIds = useMemo(() => {
    const ids = new Set<string>();
    for (const candidate of duplicatesQuery.data ?? []) {
      ids.add(candidate.patientId);
      ids.add(candidate.candidatePatientId);
    }
    return [...ids];
  }, [duplicatesQuery.data]);

  const patientsQuery = useQuery({
    queryKey: ['patients', 'duplicates', 'lookup', patientIds],
    queryFn: ({ signal }) => searchPatients({ ids: patientIds }, signal),
    enabled: patientIds.length > 0,
  });

  const patientsById = useMemo(() => {
    const map = new Map<string, PatientResponse>();
    for (const patient of patientsQuery.data ?? []) {
      map.set(patient.id, patient);
    }
    return map;
  }, [patientsQuery.data]);

  const resolve = useMutation({
    mutationFn: ({ candidateId, status }: { candidateId: string; status: ResolveStatus }) =>
      resolvePatientDuplicate(candidateId, { status }),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey });
    },
  });

  return (
    <div className="flex flex-col gap-4">
      <p className="m-0 text-sm text-ink-muted">{t('description')}</p>
      <Panel>
        <QuerySection
          query={duplicatesQuery}
          loadingLabel={t('loading')}
          isEmpty={(data) => data.length === 0}
          emptyMessage={t('emptyMessage')}
        >
          {(candidates) => (
            <ul className="m-0 flex list-none flex-col gap-3 p-0">
              {candidates.map((candidate) => (
                <CandidateRow
                  key={candidate.id}
                  candidate={candidate}
                  patientA={patientsById.get(candidate.patientId)}
                  patientB={patientsById.get(candidate.candidatePatientId)}
                  mayResolve={mayResolve}
                  pending={resolve.isPending && resolve.variables.candidateId === candidate.id}
                  onResolve={(status) => {
                    resolve.mutate({ candidateId: candidate.id, status });
                  }}
                />
              ))}
            </ul>
          )}
        </QuerySection>
        {resolve.isError ? <ErrorText message={t('resolveError')} /> : null}
      </Panel>
    </div>
  );
}

function CandidateRow({
  candidate,
  patientA,
  patientB,
  mayResolve,
  pending,
  onResolve,
}: {
  readonly candidate: PatientDuplicateCandidateResponse;
  readonly patientA: PatientResponse | undefined;
  readonly patientB: PatientResponse | undefined;
  readonly mayResolve: boolean;
  readonly pending: boolean;
  readonly onResolve: (status: ResolveStatus) => void;
}) {
  const { t } = useTranslation('patientDuplicates');

  return (
    <li className="flex flex-col gap-3 rounded-panel border border-row-line bg-surface p-4">
      <div className="flex flex-wrap items-center gap-2">
        <Badge tone="accent">{t('scoreLabel', { score: candidate.score })}</Badge>
        {candidate.reasons.map((reason) => (
          // `defaultValue` falls back to the raw stored string for a reason this build's locale
          // files do not (yet) have a label for — matching `dermatology.ts`'s own `photoAngleLabel`
          // "known angles get real prose, unknown falls back to the raw value" precedent, rather
          // than rendering nothing for a reason the server sent.
          <Badge key={reason} tone="neutral">
            {t(`reasons.${reason}`, { defaultValue: reason })}
          </Badge>
        ))}
      </div>
      <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
        <PatientSummary patient={patientA} patientId={candidate.patientId} />
        <PatientSummary patient={patientB} patientId={candidate.candidatePatientId} />
      </div>
      {mayResolve ? (
        <div className="flex flex-wrap gap-2">
          <Button
            variant="primary"
            pending={pending}
            onClick={() => {
              onResolve('confirmed_duplicate');
            }}
          >
            {t('actions.confirmDuplicate')}
          </Button>
          <Button
            variant="secondary"
            pending={pending}
            onClick={() => {
              onResolve('confirmed_distinct');
            }}
          >
            {t('actions.confirmDistinct')}
          </Button>
        </div>
      ) : null}
    </li>
  );
}

function PatientSummary({
  patient,
  patientId,
}: {
  readonly patient: PatientResponse | undefined;
  readonly patientId: string;
}) {
  const { t } = useTranslation('patientDuplicates');

  if (patient === undefined) {
    return <p className="m-0 text-sm text-ink-muted">{t('unknownPatient')}</p>;
  }

  return (
    <div className="flex flex-col gap-1 rounded-control border border-field-line bg-field p-3">
      <Link
        to={`/patients/${patientId}`}
        className="font-medium text-accent-text underline-offset-2 hover:underline"
      >
        {patient.lastName}, {patient.firstName}
      </Link>
      <p className="m-0 text-sm break-words text-ink-muted">
        {t('dobLabel')}: {formatCalendarDate(patient.dateOfBirth)} · {t('mrnLabel')}: {patient.mrn}
      </p>
    </div>
  );
}
